/**
 * PF Revamp — v2 catalogue transformer.
 *
 * Pure mapping  `ProductCatalogueV2`  →  the EXISTING `ResolvedCatalogue` UI
 * contract. This is the new impedance buffer that replaces the old
 * Salesforce-mirror `resolveCatalogue` adapter. The `Resolved*` shapes are
 * intentionally unchanged so catalogue / drawer / cart / summary UI keep
 * working untouched.
 *
 * Mapping rules:
 *   shell                       → ResolvedShellProduct
 *   shell.products (variants)   → ResolvedVariant[]
 *   variant.addons              → ResolvedOption[] (BOOLEAN toggles)
 *   variant.requiredConfiguration → ResolvedShellProduct.configurationFlows
 *   ProductCategoryV2           → FE slug + display metadata (ported from the
 *                                 old adapter's SF_CATEGORY_* tables)
 *
 * `productCode` is the identity that flows onto every resolved shape's
 * `productCode` field. Resolved `id` = `productCode` for now (opaque; never
 * parse or construct it).
 *
 * NAMES: the v2 wire carries NO name strings. Shell names,
 * descriptions and variant-section labels are resolved from i18n keyed by
 * `productGroupCode` — via the OPTIONAL `resolveGroupPresentation` resolver on
 * the context, which `useCatalogue` builds from `useTranslation`. This keeps
 * both this module and the grouper pure/unit-testable (no i18n import here).
 * Precedence for the SHELL name: group i18n → the shell's own hint (mock-
 * authored shells / the grouper's humanized fallback) → the OPTIONAL
 * `resolveProductName` resolver (individual `product:<productCode>` i18n
 * lookup, same route as `resolveGroupPresentation`) → humanized productCode.
 * Variant and addon names have no group-level overlay, so their precedence is
 * shorter: their own hint (FE-derived in the grouper, e.g. `variantLabel`) →
 * `resolveProductName` → humanized productCode. `resolveProductName` is an
 * UPGRADE over the humanized fallback in every case, never a replacement for
 * it — an unresolved productCode still degrades to a readable name, never a
 * raw key path or an empty string.
 *
 * CARRIED-BUT-UNRENDERED fields (deliberate seams, all data present):
 *   - `ResolvedVariant.bundledProducts` (P3): the drawer's white section, under
 *     the selected variant in
 *     `ProductDrawer.component.tsx`, is where the ~15-line "what's inside"
 *     member list goes (`selectedVariant.bundledProducts`, plain `Typography`).
 *   - `ResolvedVariant.dependents` (P4 = Option A): rendering these as upsells
 *     REQUIRES an `OrderProductDTO.dependentProducts` serialization path in
 *     `serializePlacement.buildProduct` — they must NOT go through
 *     `buildAddons` (that would misdeclare an orderable product as a line addon).
 *   - `ResolvedCatalogue.discountPercentage` (P5): the display-only org
 *     discount. Insertion point for the `Subtotal / Discount −X% / Total` block
 *     is `PurchaseFlowRevamp/components/cart/CartSummary.component.tsx` —
 *     between the VAT row (`cart.vat_label`) and the Total row
 *     (`cart.total_label`), applying the factor to `total` only, with `Big`.
 */

import { reportArtworkCoverage } from './productArtworkCoverage'
import { withSourceUploadFlow } from 'utils/purchaseFlowRevamp'
import { catalogueEntryRole } from 'utils/purchaseFlowRevamp/catalogueEntryRole'

import type {
  CatalogueAddonV2,
  CatalogueExtraV2,
  CatalogueVariantV2,
  ConfigurationFlowId,
  CountryCode,
  MonetaryValueV2,
  ProductCatalogueV2,
  ProductCategoryV2,
  RequiredConfigurationV2,
  ResolvedCatalogue,
  ResolvedCategory,
  ResolvedExtra,
  ResolvedOption,
  ResolvedPrice,
  ResolvedFeaturedEntry,
  ResolvedShellProduct,
  ResolvedVariant,
  ShellProductV2
} from 'models/purchaseFlowRevamp'

/** i18n-resolved shell presentation. All fields optional — an unresolved key
 *  falls back to the shell's own hint, then to a humanized code. */
export interface GroupPresentation {
  displayName?: string
  description?: string
  variantSectionLabel?: string
  /** A note under the drawer's options, e.g. how long a tour is hosted. */
  note?: string
}

export interface ToResolvedCatalogueContext {
  countryCode: CountryCode
  /** Locale string (e.g. 'de-DE'). Stamped onto `resolvedLocale`. */
  locale?: string
  /**
   * Resolves shell copy for a BE product group (see file header). Injected by
   * `useCatalogue`; omitted in pure unit tests and by mock-authored shells that
   * carry their own `displayName`.
   */
  resolveGroupPresentation?: (
    productGroupCode: string,
    category: ProductCategoryV2
  ) => GroupPresentation | undefined
  /**
   * Resolves an individual product's display name from the `product` i18n
   * namespace, keyed VERBATIM by the wire `productCode` (opaque — never
   * parsed). Injected by `useCatalogue` alongside `resolveGroupPresentation`;
   * returns `undefined` on a miss so `humanizeProductCode` stays the final
   * safety net rather than a raw key path or an empty name. This is an
   * UPGRADE over the humanized fallback, not a replacement for it.
   */
  resolveProductName?: (productCode: string) => string | undefined
  /**
   * A variant's one-line explanation, keyed by its product kind (SD / HD tour, …), so a drawer offering two
   * kinds says how they differ. Returns undefined when the kind has none.
   */
  resolveVariantDescription?: (kind: string) => string | undefined
  /** i18n for a TAB label, keyed by tab id (`product2:categories.<ID>`). */
  resolveCategoryName?: (categoryId: string) => string | undefined
}

/* -------------------------------------------------------------------------- *
 *  ProductCategoryV2 → FE category slug + display metadata
 *
 *  Ported from the old adapter (SF_CATEGORY_TO_SLUG / CATEGORY_DISPLAY_*). The
 *  enum values differ (REAL_ESTATE/EDITING/STAGING/…), so the slug mapping is
 *  re-keyed; the slug→display tables reuse the legacy click-category labels.
 * -------------------------------------------------------------------------- */

const V2_CATEGORY_TO_SLUG: Record<ProductCategoryV2, string> = {
  REAL_ESTATE: 'on_site_capture',
  EDITING: 'rendering_editing',
  STAGING: 'virtual_staging',
  AUTHORITIES_DOCUMENTS: 'authorities_documents',
  ENERGY_CERTIFICATE: 'energy_certificate',
  EVENT: 'event',
  PRODUCT: 'product',
  FOOD: 'food',
  CORPORATE: 'corporate',
}

/** Every slug the wire can produce — the set that needs a translated label. */
export const CATEGORY_SLUGS: readonly string[] = Object.values(V2_CATEGORY_TO_SLUG)

/** The slug of the editing category, where a client sends visuals to be edited rather than captured. */
export const EDITING_CATEGORY_ID = V2_CATEGORY_TO_SLUG.EDITING

const categorySlug = (category: ProductCategoryV2): string =>
  V2_CATEGORY_TO_SLUG[category] ?? String(category).toLowerCase()

/** Default display order per category slug (mirrors the old adapter). */
const CATEGORY_DISPLAY_ORDER: Record<string, number> = {
  on_site_capture: 10,
  rendering_editing: 20,
  virtual_staging: 30,
  authorities_documents: 40,
  energy_certificate: 50,
  event: 60,
  product: 70,
  food: 80,
  corporate: 90,
}

/** Human-readable display names per category slug (legacy click-category labels). */
/** English fallback only — the label comes from `product2:categories.<slug>`. */
const CATEGORY_DISPLAY_NAME: Record<string, string> = {
  on_site_capture: 'Photography',
  rendering_editing: 'Editing',
  virtual_staging: '3D',
  authorities_documents: 'Documents',
  energy_certificate: 'Energy certificate',
  event: 'Event',
  product: 'Product',
  food: 'Food',
  corporate: 'Corporate',
}

/** Static icon mapping per category slug. */
const CATEGORY_ICON_ID: Record<string, string> = {
  on_site_capture: 'photo',
  rendering_editing: 'edit',
  virtual_staging: 'staging',
  authorities_documents: 'document',
  energy_certificate: 'energy',
}

/* -------------------------------------------------------------------------- *
 *  requiredConfiguration (singular enum) → ConfigurationFlowId[]
 *
 *  CONFIRMED mappings (1:1 with existing FE flows):
 *    STAGING, FLOOR_PLAN, FLOOR_PLAN_CERTIFICATION, MEASUREMENT_ON_SITE,
 *    CONSUMPTION_CERTIFICATE, CERTIFICATE_OF_NEED.
 *
 *    AUTHORITIES_DOCUMENT → `power_of_attorney`, ONE flow and one tab. A document
 *    also collects the property's land-registration data, but that is a PAGE
 *    inside the same configurator (see the authoritiesDocument module), not a
 *    second tab — the same shape the energy certificate uses for its five pages.
 *
 *  ⚠️ BEST-EFFORT (flagged — confirm with BE/contract lead):
 *    - AI_EDITING → []. No existing FE configuration flow corresponds to AI
 *      photo editing. Defaulting to "no configurator" (renders without a
 *      configuration tab).  // TODO confirm
 * -------------------------------------------------------------------------- */

const REQUIRED_CONFIG_TO_FLOWS: Record<RequiredConfigurationV2, ConfigurationFlowId[]> = {
  STAGING: ['staging'],
  FLOOR_PLAN: ['floor_plan_config'],
  FLOOR_PLAN_CERTIFICATION: ['floor_plan_certification'],
  MEASUREMENT_ON_SITE: ['measurement'],
  CONSUMPTION_CERTIFICATE: ['consumption_certificate'],
  CERTIFICATE_OF_NEED: ['energy_certificate_of_need'],
  AUTHORITIES_DOCUMENT: ['power_of_attorney'],
  // The BE announces this on AI-editing products and the v2 order accepts it back
  // as `AiEditingConfigurationDTO`; mapping it to nothing is what left the sky
  // settings — which legacy has — off the drawer entirely.
  AI_EDITING: ['ai_editing'],
}

/* -------------------------------------------------------------------------- *
 *  Helpers
 * -------------------------------------------------------------------------- */

/** Humanize an opaque productCode into a readable fallback name. The FINAL
 *  safety net: reached only when neither the mock's own `displayName` hint
 *  nor the `resolveProductName` i18n lookup (see file header) resolved a
 *  name — never render a raw key path or an empty name instead. */
const humanizeProductCode = (productCode: string): string =>
  productCode
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())

const toResolvedPrice = (
  price: MonetaryValueV2 | null | undefined,
  productCode: string
): ResolvedPrice | null => {
  if (!price) return null
  return {
    // Synthetic opaque key — the v2 wire has no PriceBookEntry concept.
    priceBookEntryId: `pbe-${productCode}`,
    unitPrice: String(price.amount),
    currency: price.currency,
  }
}

/* -------------------------------------------------------------------------- *
 *  Addon → ResolvedOption  (BOOLEAN toggle)
 * -------------------------------------------------------------------------- */

const toResolvedOption = (
  addon: CatalogueAddonV2,
  parentVariantCode: string,
  resolveProductName?: (productCode: string) => string | undefined
): ResolvedOption => {
  const price = toResolvedPrice(addon.price, addon.productCode)
  return {
    id: addon.productCode,
    productCode: addon.productCode,
    parentVariantId: parentVariantCode,
    displayName:
      resolveProductName?.(addon.productCode) ??
      addon.displayName ??
      humanizeProductCode(addon.productCode),
    kind: 'BOOLEAN',
    // The add-on's own product kind, kept separate from the quantification kind
    // above. Notices read it (see `ResolvedOption.productKind`).
    productKind: addon.kind,
    configurationFlows: addon.requiredConfiguration && (addon.inputImagePolicy ?? 'NONE') === 'NONE'
      ? REQUIRED_CONFIG_TO_FLOWS[addon.requiredConfiguration] ?? []
      : [],
    // The relation decides where this renders, not the array it arrived in.
    role: catalogueEntryRole({
      role: addon.role,
      source: 'addons',
    }),
    enumChoices: undefined,
    min: undefined,
    max: undefined,
    defaultValue: false,
    required: false,
    prices: price ? [price] : [],
  }
}

/* -------------------------------------------------------------------------- *
 *  Extra → ResolvedExtra  (product extension)
 * -------------------------------------------------------------------------- */

/**
 * Map one wire extra onto the shape the drawer's `ExtraServicePicker`, the cart
 * badge and `buildAddons` already consume.
 *
 * `selection` is read, never assumed: the 3D floor-plan upgrade is a NUMBER
 * extra, so defaulting to BOOLEAN would price a count of 3 as one unit.
 * `allowMultiple` follows from it — only a NUMBER extra can be taken more than
 * once, and for a BOOLEAN one ordering more bills more and delivers nothing.
 */
const toResolvedExtra = (
  extra: CatalogueExtraV2,
  categoryId: string,
  resolveProductName?: (productCode: string) => string | undefined
): ResolvedExtra => {
  const price = toResolvedPrice(extra.price, extra.productCode)
  return {
    // The wire has no Salesforce id; the productCode IS the identity, and
    // `SalesforceId` is a bare string, so every id-join keeps resolving.
    id: extra.productCode,
    productCode: extra.productCode,
    categoryId,
    displayName:
      resolveProductName?.(extra.productCode) ??
      extra.displayName ??
      humanizeProductCode(extra.productCode),
    blocking: extra.requiresMainProductToWork,
    allowMultiple: extra.selection === 'NUMBER',
    prices: price ? [price] : [],
    kind: extra.selection,
    productKind: extra.kind,
    role: catalogueEntryRole({
      role: extra.role,
      source: 'extras',
    }),
  }
}

/* -------------------------------------------------------------------------- *
 *  Variant → ResolvedVariant
 * -------------------------------------------------------------------------- */

/** Depth cap for the recursive `dependents` walk. The contract says dependent
 *  nodes are terminal (empty `dependents`), so 3 is generous; the cap only
 *  exists so a cyclic/pathological payload can't hang the transform. */
const MAX_DEPENDENT_DEPTH = 3

/** A multi-unit product reads as its units of the base product, e.g. "2 × 10 photos"; null for any other
 *  product, or when the base product has no name to show. */
const multiUnitName = (
  variant: CatalogueVariantV2,
  resolveProductName?: (productCode: string) => string | undefined
): string | null => {
  if (!variant.units || !variant.baseProductCode) return null
  const baseName = resolveProductName?.(variant.baseProductCode)
  return baseName ? `${variant.units} \u00d7 ${baseName}` : null
}

const toResolvedVariant = (
  variant: CatalogueVariantV2,
  shellProductCode: string,
  resolveProductName?: (productCode: string) => string | undefined,
  depth = 0,
  resolveVariantDescription?: (kind: string) => string | undefined
): ResolvedVariant => {
  const price = toResolvedPrice(variant.price, variant.productCode)
  // Resolved the same way as `price`; the BE sends one only when there is a saving.
  const basePrice = toResolvedPrice(variant.basePrice, variant.productCode)
  return {
    id: variant.productCode,
    productCode: variant.productCode,
    documentSelectionGroup: variant.documentSelectionGroup ?? null,
    basePrice,
    shellProductId: shellProductCode,
    // A real per-product translation outranks the grouper's synthesized tier
    // label ("Basic"/"Option 7"), which is only a bridge for unnamed products.
    displayName:
      multiUnitName(variant, resolveProductName) ??
      resolveProductName?.(variant.productCode) ??
      variant.displayName ??
      humanizeProductCode(variant.productCode),
    description: variant.kind ? resolveVariantDescription?.(variant.kind) : undefined,
    active: true,
    priced: price !== null,
    price,
    timeOnSiteMin: variant.timeOnSiteMin,
    timeOnSiteMax: variant.timeOnSiteMax,
    options: variant.addons.map((a) => toResolvedOption(a, variant.productCode, resolveProductName)),
    extras: (variant.extras ?? []).map((e) =>
      toResolvedExtra(e, categorySlug(variant.category), resolveProductName)
    ),
    kind: variant.kind,
    inputImagePolicy: variant.inputImagePolicy,
    bundledProducts: variant.bundledProducts,
    bundleType: variant.bundleType ?? null,
    // This variant's OWN flows. The shell keeps the union for display and for
    // callers that only know the shell; the configuration step scopes to the
    // bought variant so a sibling's form never lands on the step.
    // A product that takes the client's files and has no step of its own is given
    // the editing configuration page here — see `withSourceUploadFlow`.
    configurationFlows: withSourceUploadFlow(
      variant.requiredConfiguration ? (REQUIRED_CONFIG_TO_FLOWS[variant.requiredConfiguration] ?? []) : [],
      variant.inputImagePolicy
    ),
    // Carried, not rendered (P3/P4 — see file header). Dependents keep the
    // PARENT's shellProductId so any id-join stays resolvable; they are not
    // members of the shell's variant picker.
    dependents:
      depth >= MAX_DEPENDENT_DEPTH
        ? []
        : (variant.dependents ?? []).map((dependent) =>
          toResolvedVariant(dependent, shellProductCode, resolveProductName, depth + 1)
        ),
  }
}

/* -------------------------------------------------------------------------- *
 *  Shell → ResolvedShellProduct
 * -------------------------------------------------------------------------- */

/**
 * Replace a bundle shell's configuration flows with the union of its MEMBERS'.
 *
 * A bundle's own `requiredConfiguration` is the backend's compatibility answer,
 * collapsed to the single most specific type so the order validator can gate on
 * it. That collapse is lossy for the UI: a pack holding a living-area
 * calculation AND a floor plan reports only the certification, and the
 * floor-plan form would never be shown. Each member publishes its own
 * `requiredConfiguration` and `inputImagePolicy`, so the steps are derived from
 * them instead — one per member that needs one, de-duplicated.
 */
/** Fallback member name from its `kind`. Lower-cases first, unlike
 *  `humanizeProductCode`: a kind is SHOUTING_SNAKE_CASE and would otherwise
 *  render as "MOBILE APPLICATION FLOOR PLAN" in a card tag. */
const humanizeKind = (kind: string): string =>
  (kind || 'product')
    .replace(/[-_]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())

/**
 * Name a pack from the product code it actually sells, not from its shell id.
 *
 * A bundle shell's id is synthetic (`bundle_<productCode>`), so the ordinary
 * shell-name lookup — which keys on the shell id — can never hit: it degrades to
 * humanizing the synthetic id and a pack reads "Bundle P 3410" instead of the
 * name the catalogue already has for it ("2 flats - 10 photos per flat"). The
 * real code lives on the single variant.
 */
const withBundleName = (
  shell: ResolvedShellProduct,
  ctx: ToResolvedCatalogueContext
): ResolvedShellProduct => {
  const productCode = shell.variants[0]?.productCode
  const resolved = productCode ? ctx.resolveProductName?.(productCode) : undefined
  return resolved ? { ...shell, displayName: resolved } : shell
}

const withNamedBundleMembers = (
  shell: ResolvedShellProduct,
  ctx: ToResolvedCatalogueContext
): ResolvedShellProduct => ({
  ...shell,
  variants: shell.variants.map((variant) => ({
    ...variant,
    bundledProducts: (variant.bundledProducts ?? []).map((member) => ({
      ...member,
      displayName: ctx.resolveProductName?.(member.productCode) ?? humanizeKind(member.kind),
    })),
  })),
})

const withBundleConfigurationFlows = (shell: ResolvedShellProduct): ResolvedShellProduct => {
  const members = shell.variants.flatMap((v) => v.bundledProducts ?? [])
  if (members.length === 0) return shell

  const flows: ConfigurationFlowId[] = []
  for (const member of members) {
    const memberFlows = withSourceUploadFlow(
      member.requiredConfiguration ? (REQUIRED_CONFIG_TO_FLOWS[member.requiredConfiguration] ?? []) : [],
      member.inputImagePolicy
    )
    for (const flow of memberFlows) {
      if (!flows.includes(flow)) flows.push(flow)
    }
  }
  return { ...shell, configurationFlows: flows }
}

/** Collect the shell's configuration flows from its variants'
 *  `requiredConfiguration`, mapped + de-duplicated (order preserved). */
const deriveConfigurationFlows = (shell: ShellProductV2): ConfigurationFlowId[] => {
  const flows: ConfigurationFlowId[] = []
  for (const variant of shell.products) {
    // Resolved PER VARIANT before the union, because the editing page is derived
    // from the variant's own `inputImagePolicy`: a shell can hold one product that
    // takes the client's files beside one that does not.
    const variantFlows = withSourceUploadFlow(
      variant.requiredConfiguration ? (REQUIRED_CONFIG_TO_FLOWS[variant.requiredConfiguration] ?? []) : [],
      variant.inputImagePolicy
    )
    for (const flow of variantFlows) {
      if (!flows.includes(flow)) flows.push(flow)
    }
  }
  return flows
}

const toResolvedShell = (
  shell: ShellProductV2,
  displayOrder: number,
  ctx: ToResolvedCatalogueContext
): ResolvedShellProduct => {
  const variants = shell.products.map((v) => toResolvedVariant(v, shell.productCode, ctx.resolveProductName, 0, ctx.resolveVariantDescription))
  const configurationFlows = deriveConfigurationFlows(shell)

  // Anything other than NONE means the product takes input images.
  const requiresUpload = shell.products.some((v) => v.inputImagePolicy !== 'NONE')

  // i18n copy for the BE group (see file header for the precedence chain).
  const presentation = shell.productGroupCode
    ? ctx.resolveGroupPresentation?.(shell.productGroupCode, shell.category)
    : undefined

  return {
    id: shell.productCode,
    productCode: shell.productCode,
    categoryId: categorySlug(shell.category),
    // The TAB is a different axis from the wire category and is owned by
    // Salesforce on the GROUP. `categoryId` is untouched — it still
    // drives meeting gating and the legacy order category.
    tabId: shell.catalogueCategory ?? categorySlug(shell.category),
    catalogueCategory: shell.catalogueCategory,
    displayName:
      presentation?.displayName ??
      shell.displayName ??
      ctx.resolveProductName?.(shell.productCode) ??
      humanizeProductCode(shell.productCode),
    description: presentation?.description ?? shell.description,
    preview: shell.preview,
    displayOrder: shell.displayOrder ?? displayOrder,
    active: true,
    requiresConfiguration: configurationFlows.length > 0,
    configurationFlowId: configurationFlows[0],
    configurationFlows,
    variants,
    variantSectionLabel: presentation?.variantSectionLabel ?? shell.variantSectionLabel,
    note: presentation?.note,
    extraServices: [],
    requiresUpload,
    // D1 — `kind` was never populated under v2, which silently no-op'd
    // `resolveTabs`' fallback flow resolver and unscoped the legacy
    // instruction-catalogue call. The grouper stamps it from the wire products.
    kind: shell.kind ?? shell.products[0]?.kind,
    productGroupCode: shell.productGroupCode,
    // `productType` has NO v2 source — deliberately left undefined.
  }
}

/* -------------------------------------------------------------------------- *
 *  Category derivation (ported from the old adapter)
 * -------------------------------------------------------------------------- */

/**
 * Tabs, keyed on `tabId` — the Salesforce catalogue category when the group has
 * one, else the legacy product-category slug.
 *
 * A Salesforce tab takes its position from the LOWEST group display order inside
 * it, so the tab order is data too: the numbering is blocked per category, so the
 * blocks order the tabs without a second table to keep in step. Legacy slugs keep
 * their own map, which is what every tab falls back to until the data lands.
 */
const resolveCategories = (
  shells: ResolvedShellProduct[],
  resolveCategoryName?: (tabId: string) => string | undefined
): ResolvedCategory[] => {
  const seen = new Map<string, ResolvedCategory>()
  for (const shell of shells) {
    const id = shell.tabId
    const placed = shell.catalogueCategory ? shell.displayOrder : undefined
    const existing = seen.get(id)
    if (existing) {
      if (placed !== undefined) existing.displayOrder = Math.min(existing.displayOrder, placed)
      continue
    }
    seen.set(id, {
      id,
      // The label is translated (`product2:categories.<id>`); the English map
      // below is only the fallback for an id with no copy yet.
      displayName: resolveCategoryName?.(id) ?? CATEGORY_DISPLAY_NAME[id] ?? id,
      displayOrder: placed ?? CATEGORY_DISPLAY_ORDER[id] ?? 999,
      iconId: CATEGORY_ICON_ID[id],
    })
  }
  return [...seen.values()].sort((a, b) => a.displayOrder - b.displayOrder)
}

/* -------------------------------------------------------------------------- *
 *  Entry point
 * -------------------------------------------------------------------------- */

/**
 * Pure transformer — converts the v2 catalogue + context into the resolved
 * catalogue the UI consumes.
 *
 * `discountPercentage` is carried onto `ResolvedCatalogue` but NOT
 * applied: every resolved price stays gross, matching the wire contract's
 * "the client applies it for display only" + the authoritative discounted/VAT
 * total being computed at the pricing/order step. See the file header for the
 * CartSummary insertion point (P5).
 *
 * `extras` (the legacy extra-service flat list) is empty under v2 — the new
 * contract models upsells as nested `dependents` (carried on each
 * `ResolvedVariant`, unrendered — P4 Option A), not a separate extra-service
 * relation.
 */
/**
 * Flat, deduped extras across every variant, keyed by id.
 *
 * The wire scopes extras per product (a floor-plan band carries only its own
 * 3D band), but `buildAddons` resolves an extra by id off the catalogue, so it
 * needs the union. First occurrence wins:
 * within one catalogue an extra code is the same product at the same price.
 */
const unionExtras = (shellProducts: ResolvedShellProduct[]): ResolvedExtra[] => {
  const byId = new Map<string, ResolvedExtra>()
  for (const shell of shellProducts) {
    for (const variant of shell.variants) {
      for (const extra of variant.extras) {
        if (!byId.has(extra.id)) byId.set(extra.id, extra)
      }
    }
  }
  return [...byId.values()]
}

/**
 * Maps the backend's featured product codes onto the shells that open them. A code resolves to its
 * bundle shell, else to the grouped shell holding that variant; one the catalogue does not serve is
 * skipped. Without the list (an older backend) every bundle is featured, which is what the section
 * showed before.
 */
const resolveFeatured = (
  featured: string[] | undefined,
  groupedShells: ResolvedShellProduct[],
  bundles: ResolvedShellProduct[]
): ResolvedFeaturedEntry[] => {
  if (!featured) {
    return bundles.flatMap((shell) => (shell.variants[0] ? [{ shell, variantId: shell.variants[0].id }] : []))
  }
  return featured.flatMap((code) => {
    const bundle = bundles.find((shell) => shell.variants.some((v) => v.id === code))
    if (bundle) return [{ shell: bundle, variantId: code }]
    const shell = groupedShells.find((s) => s.variants.some((v) => v.id === code))
    return shell ? [{ shell, variantId: code }] : []
  })
}

export const toResolvedCatalogue = (
  v2: ProductCatalogueV2,
  ctx: ToResolvedCatalogueContext
): ResolvedCatalogue => {
  const groupedShells = v2.products
    .map((shell, i) => toResolvedShell(shell, (i + 1) * 10, ctx))
    .sort((a, b) => a.displayOrder - b.displayOrder)

  const bundles = (v2.bundles ?? []).map((shell, i) => withBundleConfigurationFlows(
    withNamedBundleMembers(withBundleName({ ...toResolvedShell(shell, (i + 1) * 10, ctx), isBundle: true }, ctx), ctx)
  ))

  // Categories come from the GROUPED shells alone: a bundle belongs in the
  // Featured carousel, and a bundle listed here would also render inside a
  // category section. `shellProducts` carries both, because that is the array
  // every cart-line lookup searches.
  const categories = resolveCategories(groupedShells, ctx.resolveCategoryName)
  const shellProducts = [...groupedShells, ...bundles]

  // Which groups have no Salesforce thumbnail and render the placeholder tile. Reports once, or not at all.
  reportArtworkCoverage(groupedShells, ctx.countryCode)

  return {
    categories,
    shellProducts,
    bundles,
    featured: resolveFeatured(v2.featured, groupedShells, bundles),
    extras: unionExtras(shellProducts),
    resolvedCountryCode: ctx.countryCode,
    resolvedLocale: ctx.locale ?? 'en',
    fetchedAt: new Date().toISOString(),
    discountPercentage: v2.discountPercentage ?? null,
    stagingRenovation: v2.stagingRenovation ?? null,
  }
}
